import { MultiSelectFilter, type MultiSelectOption } from '../../components/ui/MultiSelectFilter';
import type { ReportingFilterOptions } from '../../types/reporting';
import { buildTypeOptions } from '../../lib/reportingCampaignTypes';
import { availableOptions } from '../../lib/reportingFacets';

// The selected filter arrays (everything except the date range, which the picker
// owns). Empty array / omitted = no constraint.
export interface ReportingFilterSelection {
  campaignTypes: string[]; // Type — curated campaign-type keys (see reportingCampaignTypes)
  brandAccountIds: string[];
  groupAccountIds: string[];
  industries: string[];
  accountManagerIds: string[];
  countries: string[];
  currencies: string[];
  // §3b — the attribution-based Account filter + optional campaign-name tokens
  // (the token input lives above the campaign table, not in this bar).
  accountIds: string[];
  campaignSearchTokens: string[];
  // Per-level "name contains" (Ad set · Ad) — TOKEN ARRAYS, exactly like the
  // campaign tokens above: several tokens OR within a level, levels AND with
  // each other. Real columns at their own grain — see reportingNameFilters.
  adsetName: string[];
  adName: string[];
}

export const EMPTY_FILTER_SELECTION: ReportingFilterSelection = {
  campaignTypes: [],
  brandAccountIds: [],
  groupAccountIds: [],
  industries: [],
  accountManagerIds: [],
  countries: [],
  currencies: [],
  accountIds: [],
  campaignSearchTokens: [],
  adsetName: [],
  adName: [],
};

function humanizeIndustry(i: string): string {
  return i.replace(/_/g, ' ');
}

const opt = (value: string, label: string): MultiSelectOption => ({ value, label });

// The 5 spec filters (Type, Brand, Industry, Account Manager, Country) + the
// optional 6th Currency filter (collapses money stats to a single currency).
// Options are data-derived from /filter-options.
export function ReportingFilterBar({
  options,
  selection,
  onChange,
}: {
  options: ReportingFilterOptions | undefined;
  selection: ReportingFilterSelection;
  onChange: (next: ReportingFilterSelection) => void;
}) {
  const o = options;
  const facets = o?.accountFacets ?? [];
  const patch = (p: Partial<ReportingFilterSelection>) => onChange({ ...selection, ...p });

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Type — curated from the distinct objectives (OUTCOME_LEADS splits into
          On-Meta / Website / All Lead); selected keys drive the SQL predicates.
          Type does NOT cascade (campaign-level, not an account attribute). */}
      <MultiSelectFilter
        label="Type"
        options={buildTypeOptions(o?.objectives ?? []).map((t) => opt(t.key, t.label))}
        selected={selection.campaignTypes}
        onChange={(v) => patch({ campaignTypes: v })}
      />
      {/* Brand / Group / Industry / AM / Account / Country CASCADE: each list is
          the exclude-self facet over the other selected account-level filters. */}
      <MultiSelectFilter
        label="Brand"
        options={availableOptions(facets, selection, 'brand').map((b) => opt(b.id, b.name))}
        selected={selection.brandAccountIds}
        onChange={(v) => patch({ brandAccountIds: v })}
      />
      {/* Group — the nearest group-type ancestor (owner group). */}
      <MultiSelectFilter
        label="Group"
        options={availableOptions(facets, selection, 'group').map((g) => opt(g.id, g.name))}
        selected={selection.groupAccountIds}
        onChange={(v) => patch({ groupAccountIds: v })}
      />
      <MultiSelectFilter
        label="Industry"
        options={availableOptions(facets, selection, 'industry').map((i) =>
          opt(i.id, humanizeIndustry(i.name)),
        )}
        selected={selection.industries}
        onChange={(v) => patch({ industries: v })}
      />
      <MultiSelectFilter
        label="AM"
        options={availableOptions(facets, selection, 'am').map((a) => opt(a.id, a.name))}
        selected={selection.accountManagerIds}
        onChange={(v) => patch({ accountManagerIds: v })}
      />
      {/* §3b — Account filter resolves via attribution (token / standalone owner). */}
      <MultiSelectFilter
        label="Account"
        options={availableOptions(facets, selection, 'account').map((a) => opt(a.id, a.name))}
        selected={selection.accountIds}
        onChange={(v) => patch({ accountIds: v })}
      />
      <MultiSelectFilter
        label="Country"
        options={availableOptions(facets, selection, 'country').map((c) => opt(c.id, c.name))}
        selected={selection.countries}
        onChange={(v) => patch({ countries: v })}
      />
      <MultiSelectFilter
        label="Currency"
        options={(o?.currencies ?? []).map((v) => opt(v, v))}
        selected={selection.currencies}
        onChange={(v) => patch({ currencies: v })}
      />
    </div>
  );
}
